<template>
  <div>
    <Header v-if="orderDetail">
      <template v-slot:left>
        <FeatherIcon icon="chevron-left" @click="goBack" />
      </template>
      <template v-slot:right>
        <BButtonGroup
          v-if="transitionSource === 'Search'"
          class="paginate"
          size="sm"
        >
          <BButton
            variant="bg-gray-06"
            @click="prevOrder"
            class="button"
            :class="{ disabled: !hasPrevOrder }"
            :disabled="!hasPrevOrder"
            data-testid="prev-button"
            ><FeatherIcon icon="arrow-up"
          /></BButton>
          <BButton
            variant="bg-gray-06"
            @click="nextOrder"
            class="button"
            :class="{ disabled: !hasNextOrder }"
            :disabled="!hasNextOrder"
            data-testid="next-button"
            ><FeatherIcon icon="arrow-down"
          /></BButton>
        </BButtonGroup>
      </template>
      <span v-if="isInitializeComplete">
        {{ orderDateTime }}
      </span>
      <span v-if="isOrderFetchingFailed">エラー</span>
    </Header>

    <div v-if="isInitializeComplete && orderDetail" class="pb-5 mb-5">
      <!-- メニュータブ用のスペース -->
      <div class="pb-4 pt-3" />

      <OrderCard
        ref="orderCard"
        :order="orderDetail"
        v-model:selectedWorkTimeOptionId="selectedWorkTimeOptionId"
        :evaluationTotalAverage="evaluationTotalAverage"
        :evaluationCount="evaluationCount"
        :orderService="orderService"
        displayCarfare
        :isLogin="isLogin"
        isCarousel
        :isFavorite="isFavorite"
        :isMutedOffice="isMutedOffice"
        :application="application"
        :isRegisteredWatchLater="isRegisteredWatchLater"
        @favorite-change="onClickFavoriteOffice"
        @change-muted-office="onClickMuteButton"
        @change-watch-later="onWatchLaterChange"
        :showOfficeFavoriteButton="showOfficeFavoriteButton"
      />
      <SameOfficeOrders
        :order-detail="orderDetail"
        :applicable-orders="applicableOrders"
        class="mt-2"
      />
      <OrderIntroContents :orderDetail="orderDetail" class="mt-2" />

      <OrderTabContents
        :order="orderDetail"
        :favoriteOffice="favoriteOffice"
        :isLogin="isLogin"
        :anonymousEvaluations="anonymousEvaluations"
        :evaluationCount="evaluationCount"
        :evaluationTotalAverage="evaluationTotalAverage"
        class="mt-2"
        @click-notice-of-employment="onClickNoticeOfEmployment"
        @click-attachment-file="onClickAttachmentFile"
        @click-view-root="onClickViewRoot"
      />

      <OrderFixedButton
        v-if="!isLogin || canApply || isFetchingCanApply"
        @click="onClickApply"
        :isShownWatchLaterButton="isShownWatchLaterButton"
        :isRegisteredWatchLater="isRegisteredWatchLater"
        :isDisabled="isFetchingCanApply"
        :abTestGroup3300="abTestGroup3300"
        enabledVariant="primary"
        @change-watch-later="onWatchLaterChange"
        data-testid="apply-button"
      >
        <template v-if="isFetchingCanApply">応募可否確認中</template>
        <template v-else>申し込みへ進む</template>
      </OrderFixedButton>

      <template v-else>
        <OrderFixedButton
          v-if="!hasRegisteredProfile"
          @click="onClickRegistration"
          :isShownWatchLaterButton="isShownWatchLaterButton"
          :isRegisteredWatchLater="isRegisteredWatchLater"
          :abTestGroup3300="abTestGroup3300"
          enabledVariant="primary"
          @change-watch-later="onWatchLaterChange"
        >
          応募する
        </OrderFixedButton>

        <RouterLink
          v-else-if="!hasQualification"
          :to="{ name: 'MypageQualificationList' }"
          data-testid="qualification-link"
        >
          <OrderFixedButton
            :isShownWatchLaterButton="isShownWatchLaterButton"
            :isRegisteredWatchLater="isRegisteredWatchLater"
            :abTestGroup3300="abTestGroup3300"
            enabledVariant="primary"
            @change-watch-later="onWatchLaterChange"
            hasSub
            @click="onClickApplyingButton"
            data-testid="require-qualification-button"
          >
            資格登録にすすむ
            <template #sub>応募には資格情報の入力が必要です</template>
          </OrderFixedButton>
        </RouterLink>

        <RouterLink
          v-else-if="!hasBankAccount"
          :to="{ name: 'SetBank' }"
          data-testid="bank-link"
        >
          <OrderFixedButton
            :isShownWatchLaterButton="isShownWatchLaterButton"
            :isRegisteredWatchLater="isRegisteredWatchLater"
            :abTestGroup3300="abTestGroup3300"
            enabledVariant="primary"
            @change-watch-later="onWatchLaterChange"
            hasSub
            @click="onClickApplyingButton"
            data-testid="require-bank-account-button"
          >
            口座登録にすすむ
            <template #sub>応募には口座情報の入力が必要です</template>
          </OrderFixedButton>
        </RouterLink>

        <RouterLink
          v-else-if="!hasRegisteredBusinessInfo"
          :to="{ name: 'MypageBusinessInfo' }"
          data-testid="business-info-link"
        >
          <OrderFixedButton
            :isShownWatchLaterButton="isShownWatchLaterButton"
            :isRegisteredWatchLater="isRegisteredWatchLater"
            :abTestGroup3300="abTestGroup3300"
            enabledVariant="primary"
            @change-watch-later="onWatchLaterChange"
            hasSub
            @click="onClickApplyingButton"
            data-testid="require-business-info-button"
          >
            経験・転職意欲登録にすすむ
            <template #sub>
              応募には経験・転職意欲の入力が必要です
            </template>
          </OrderFixedButton>
        </RouterLink>

        <OrderFixedButton
          v-else-if="application"
          :isShownWatchLaterButton="isShownWatchLaterButton"
          :isRegisteredWatchLater="isRegisteredWatchLater"
          @change-watch-later="onWatchLaterChange"
          isDisabled
          data-testid="applied-button"
        >
          応募済みです
        </OrderFixedButton>

        <OrderFixedButton
          v-else
          :isShownWatchLaterButton="isShownWatchLaterButton"
          :isRegisteredWatchLater="isRegisteredWatchLater"
          @change-watch-later="onWatchLaterChange"
          hasSub
          isDisabled
        >
          応募できません
          <template #sub>
            {{ canNotApplyReason }}
          </template>
        </OrderFixedButton>
      </template>
    </div>
    <div v-if="isOrderFetchingFailed" class="order-fetch-failed">
      <p class="mb-4">お仕事の取得に失敗しました</p>

      <AppButton @click="goSearch">お仕事検索に戻る</AppButton>
    </div>
    <OfficeMuteModal
      v-if="orderDetail"
      :isShown="isShownMutedOfficeModal"
      :officeName="orderDetail.office.name"
      @submit="onSubmitMuteOfficeModal"
      @cancel="onCloseMuteOfficeModal"
    />
  </div>
</template>

<script lang="ts">
import { Component, Hook, Vue, Watch, toNative } from "vue-facing-decorator";
import FeatherIcon from "@/components/atoms/other/FeatherIcon.vue";
import Header from "@/components/organisms/common/Header.vue";
import OrderCard from "@/components/organisms/orders/OrderCard.vue";
import OrderIntroContents from "@/components/organisms/orders/OrderIntroContents.vue";
import SameOfficeOrders from "@/components/organisms/orders/SameOfficeOrders.vue";
import OrderTabContents from "@/components/organisms/orders/OrderTabContents.vue";
import OrderFixedButton from "@/components/organisms/orders/OrderFixedButton.vue";
import { applicationService } from "@/app/services/ApplicationService";
import { favoriteService } from "@/app/services/FavoriteService";
import { officeService } from "@/app/services/OfficeService";
import { orderService } from "@/app/services/OrderService";
import { masterStore } from "@/store/master";
import { authStore } from "@/store/auth";
import { orderStore } from "@/store/order";
import { ErrorType, showErrorModal } from "@/utils/showErrorModal";
import { salaryService } from "@/app/services/SalaryService";
import { FavoriteOffice } from "@/types/office/FavoriteOffice";
import { NavigationGuardNext, RouteLocation } from "vue-router";
import { sendAdjustEvent } from "@/plugins/adjust";
import { sendFirebaseCustomEvent } from "@/plugins/firebase";
import { UserQualification } from "@/types/qualification/UserQualification";
import { accountService } from "@/app/services/AccountService";
import { openInNewWindow } from "@/utils/browserUtil";
import { makeGoogleMapConfirmRouteUrl } from "@/utils/googlemap";
import { BButtonGroup } from "bootstrap-vue";
import Loading from "@/components/organisms/common/Loading.vue";
import { AnonymousEvaluation } from "@/types/order/AnonymousEvaluation";
import { OrderWorkTimeOption } from "@/types/order/OrderWorkTimeOption";
import FormRadioGroup from "@/components/molecules/form/FormRadioGroup.vue";
import { routerPush } from "@/utils/routerPush";
import { captureException } from "@/plugins/sentry";
import { Application } from "@/types/order/Application";
import { inAppReviewService } from "@/app/services/InAppReviewService";
import {
  AnalyzeWatchLaterEvent,
  eventService
} from "@/app/services/EventService";
import { getSafeAreaTop } from "@/utils/domUtil";
import { SlimOrder } from "@/types/order/Order";
import { useLoginModal } from "@/utils/modal";
import { formatFromUnixTimestamp, startOfToday } from "@/utils/date";
import OfficeMuteModal from "@/components/organisms/common/OfficeMuteModal.vue";
import { AbTest3300, AbTest2807 } from "@/app/services/AbTestService";
import { h } from "vue";
import AppButton from "@/components/atoms/button/AppButton.vue";

type CanApplyRes = {
  canApply: boolean;
  canNotApplyReason: string;
};

const SCROLL_OFFSET = -90;

// ステータスバー等の高さ
const safeAreaTop = getSafeAreaTop();

@Component({
  components: {
    FeatherIcon,
    Header,
    OrderCard,
    OrderIntroContents,
    OfficeMuteModal,
    SameOfficeOrders,
    OrderTabContents,
    OrderFixedButton,
    BButtonGroup,
    FormRadioGroup,
    Loading,
    AppButton
  }
})
class OrdersDetail extends Vue {
  apiUrl = process.env.VUE_APP_API_URL_BASE;
  canApply = true;
  isFetchingCanApply = false;
  hasBankAccount = false;
  canNotApplyReason = "";
  favoriteOffice?: FavoriteOffice | null = null;
  isMutedOffice = false;
  isShownMutedOfficeModal = false;
  isInitializeComplete = false;
  isOrderFetchingFailed = false;
  evaluationTotalAverage: number | null = null;
  evaluationCount = 0;
  qualifications: UserQualification[] = [];
  anonymousEvaluations: AnonymousEvaluation[] = [];
  // 直近応募可能な案件
  applicableOrders: SlimOrder[] = [];
  selectedWorkTimeOptionId: number | null = null;
  application: Application | null = null;
  isRegisteredWatchLater = false;
  showOfficeFavoriteButton = true;

  loginModal = useLoginModal();
  segmentNumber2603: number | null = null;

  orders: SlimOrder[] = [];

  get isLogin() {
    return authStore.existToken;
  }

  get orderId() {
    return this.$route.params["orderId"] as string;
  }

  get transitionSource() {
    return (this.$route.query["transitionSource"] as string) || "Other";
  }

  get shareKey() {
    return (this.$route.query["shareKey"] as string) || undefined;
  }

  get abTestGroup3300(): "B" | "C" {
    return AbTest3300.getTestGroup();
  }

  get orderDateTime() {
    const date = orderService.getFormattedDateWithSlash(this.orderDetail);
    const selectedWorkTimeOption = this.selectedWorkTimeOption;

    let timeRange: string;
    if (this.application) {
      timeRange = orderService.getFormattedWorkTimeRangeFromDate(
        this.application.appliedStartWorkAt,
        this.application.appliedFinishWorkAt
      );
    } else if (selectedWorkTimeOption) {
      timeRange = orderService.getFormattedWorkTimeRangeFromDate(
        selectedWorkTimeOption.startWorkAt,
        selectedWorkTimeOption.finishWorkAt
      );
    } else {
      return date;
    }
    return `${date} ${timeRange}`;
  }

  get hasRegisteredProfile() {
    return authStore.user?.hasRegisteredProfile;
  }

  get hasRegisteredBusinessInfo() {
    if (!authStore.user) return false;
    if (this.orderDetail.allowUnqualified) return true;
    return authStore.user.experiences.length > 0;
  }

  get orderDetail() {
    return orderStore.orderDetail!;
  }

  get orderService() {
    return masterStore.getServiceNameById(this.orderDetail.service.id);
  }

  get hasQualification() {
    if (this.orderDetail.allowUnqualified) return true;
    return this.qualifications.length > 0;
  }

  get hasNextOrder() {
    const index = this.findOrderIndex();
    return this.orders.length > index + 1;
  }

  get hasPrevOrder() {
    const index = this.findOrderIndex();
    return index > 0;
  }

  get workTimeOptionChoices() {
    return this.orderDetail.workTimeOptions.map(option => {
      if (!option) {
        return {
          value: 0,
          text: ""
        };
      }
      const workRangeLabel = orderService.getFormattedWorkTimeRangeFromDate(
        option.startWorkAt,
        option.finishWorkAt
      );
      return {
        value: option.id,
        text: workRangeLabel
      };
    });
  }

  get isWorkTimeOptionSelected() {
    return this.selectedWorkTimeOptionId !== null;
  }

  get selectedWorkTimeOption(): OrderWorkTimeOption | undefined {
    return this.orderDetail.workTimeOptions.find(
      option => option.id === Number(this.selectedWorkTimeOptionId)
    );
  }

  get isFavorite(): boolean {
    return !!this.favoriteOffice && this.favoriteOffice.isFavorite;
  }

  get isShownWatchLaterButton() {
    return orderService.isWatchLaterButtonVisible(this.orderDetail);
  }

  findOrderIndex() {
    return this.orders.findIndex(order => order.id === Number(this.orderId));
  }

  async onClickApplyingButton() {
    try {
      orderStore.addToWatchLaterOrderList(this.orderDetail);
      this.isRegisteredWatchLater = true;
    } catch (e) {
      await showErrorModal(this, e as ErrorType);
    }
  }

  async created() {
    await this.init(this.orderId);
  }

  // 別の案件詳細画面へ遷移する時に実行する
  @Hook
  beforeRouteUpdate(
    to: RouteLocation,
    from: RouteLocation,
    next: NavigationGuardNext
  ) {
    next();
    if (to.hash) return;
    this.init(to.params.orderId as string);
  }

  async goBack() {
    if (this.transitionSource === "Search") {
      await this.$router.push({
        name: this.transitionSource,
        query: { focusedOrderId: this.orderId }
      });
    } else if (!this.hasHistory()) {
      this.goSearch();
    } else {
      this.$router.go(-1);
    }
  }

  hasHistory() {
    return window.history.length > 2;
  }

  async goSearch() {
    await this.$router.replace({ name: "Search" });
  }

  async init(orderId: string) {
    try {
      this.$loading.show();
      // 事業所ミュートした際に対象の案件をorderListから削除するため、ここで元のorderListを保持しておく
      this.orders = orderStore.orderList || [];
      try {
        this.isInitializeComplete = false;
        this.isOrderFetchingFailed = false;
        await orderStore.fetchOrderDetail({
          orderId: Number(orderId),
          ignoreWorkerService: false,
          shareKey: this.shareKey,
          transitionSource: this.transitionSource
        });
      } catch (error) {
        this.isOrderFetchingFailed = true;
        return;
      }
      if (authStore.existToken) {
        await Promise.all([
          this.fetchFavoriteOffice(),
          this.fetchMutedOffice(),
          this.fetchBankAccount(),
          this.fetchQualification(),
          accountService.updateUserData(),
          this.fetchAnonymousEvaluations(),
          this.fetchMyApplication()
        ]);
      }
      this.fetchSameOfficeOrder();
      this.fetchEvaluations();
      this.selectedWorkTimeOptionId =
        orderStore.orderDetail?.workTimeOptions.length === 1
          ? orderStore.orderDetail?.workTimeOptions[0].id
          : null;
      this.isRegisteredWatchLater = orderStore.watchLaterOrderList.some(
        order => order.id === Number(orderId)
      );
      if (
        !this.orderDetail.isHiring &&
        this.selectedWorkTimeOptionId === null
      ) {
        // 終了案件でなおかつ勤務時間が複数ある場合、selectedWorkTimeOptionIdのwatchが走らないのでここでupdateCanApplyを呼ぶ
        this.updateCanApply();
      }

      AbTest2807.registerEvent(AbTest2807.eventTypes.entry);
      AbTest3300.registerEvent(AbTest3300.eventTypes.entry);
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
    this.isInitializeComplete = true;
  }

  async fetchAnonymousEvaluations() {
    this.anonymousEvaluations = await orderService.fetchAnonymousEvaluations(
      this.orderDetail.office.id
    );
  }

  async openOrderByIndex(index: number) {
    if (this.orders.length <= index) return;
    await this.$router.push({
      name: "OrdersDetail",
      params: { orderId: String(this.orders[index].id) },
      query: { transitionSource: this.transitionSource }
    });
  }

  async nextOrder() {
    await this.openOrderByIndex(this.findOrderIndex() + 1);
  }

  async prevOrder() {
    await this.openOrderByIndex(this.findOrderIndex() - 1);
  }

  async onClickNoticeOfEmployment() {
    // 申込済みの案件かどうかを確認
    try {
      this.$loading.show();
      const url = await this.getPdfUrl();
      if (url) {
        openInNewWindow(url);
      }
    } catch (error) {
      showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async getPdfUrl() {
    // 応募ありの場合、自身の応募が紐づいた労働条件通知書の URL を返す
    if (this.application) {
      const res = await orderService.getFixedNoticeOfEmploymentPdfUrl(
        this.application.id
      );
      return res.url;
    }

    // 募集が終了している場合、時間オプション1件目の内容を利用する
    if (!this.orderDetail.isHiring) {
      const res = await orderService.getNotFixedNoticeOfEmploymentPdfUrl(
        this.orderDetail.id,
        this.orderDetail.workTimeOptions[0].id
      );
      return res.url;
    }

    // ここに到達するのは公開中かつ自身が応募していない案件の場合
    // この場合、勤務時間の選択を必須とする
    if (!this.isWorkTimeOptionSelected) {
      await this.showErrorMessageWithScrollToWorkTime();
      return;
    }

    if (!this.selectedWorkTimeOption) {
      // ここに到達することはないはず
      throw new Error("勤務時間が見つかりません");
    }

    const res = await orderService.getNotFixedNoticeOfEmploymentPdfUrl(
      this.orderDetail.id,
      this.selectedWorkTimeOption.id
    );
    return res.url;
  }

  onClickAttachmentFile() {
    openInNewWindow(this.orderDetail.attachmentFile!);
  }

  async showLoginModal() {
    const isConfirmed = await this.loginModal.showLoginModal();
    if (isConfirmed) {
      await routerPush(this, { name: "Welcome" });
    }
  }

  async onClickFavoriteOffice() {
    if (!this.isLogin) {
      await this.showLoginModal();
      return;
    }
    if (this.isMutedOffice && !this.isFavorite) {
      const modalMessage = [
        "お気に入りとミュートは同時に設定できません",
        "ミュートを解除してからお気に入り登録してください"
      ];
      const messageVNode = modalMessage.map(v =>
        h("p", { class: ["text-size-14"] }, [v])
      );
      await this.$bvModal.msgBoxOk(messageVNode, {
        centered: true,
        hideHeader: true,
        okOnly: true
      });
      return;
    }
    try {
      this.$loading.show();
      // favoriteOfficeが登録されているかによってAPIを変える
      if (this.favoriteOffice === undefined || this.favoriteOffice === null) {
        // 未登録の場合、新規登録
        this.favoriteOffice = await this.createFavoriteOffices();
      } else {
        // 登録済みの場合、更新
        this.favoriteOffice = await this.updateFavoriteOffices();
      }
      this.$bvToast.toast(
        this.favoriteOffice.isFavorite
          ? "事業所のお気に入りに追加しました"
          : "事業所のお気に入りを削除しました",
        {
          toastClass: ["toast-custom", "toast-custom-thin-header-page"],
          toaster: "b-toaster-top-full",
          autoHideDelay: 2000
        }
      );
      if (this.favoriteOffice.isFavorite) {
        await inAppReviewService.launchInAppReview("FAVORITE_OFFICE");
      }
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  removeMutedOfficeOrderFromOrderStore() {
    if (orderStore.orderList) {
      orderStore.setOrderList(
        orderStore.orderList.filter(
          order => order.office.id !== this.orderDetail.office.id
        )
      );
    }
    orderStore.setIndividualOfferOrderList(
      orderStore.individualOfferOrderList.filter(
        order => order.office.id !== this.orderDetail.office.id
      )
    );
    orderStore.setLimitedOrderListCache(
      (orderStore.limitedOrderListCache ?? []).filter(
        order => order.office.id !== this.orderDetail.office.id
      )
    );
    orderStore.clearLimitedOrderListCountCache();
  }

  async updateMutedOffice({ reason }: { reason?: string } = {}) {
    try {
      this.$loading.show();
      await accountService.updateMutedOffice({
        officeId: this.orderDetail.office.id,
        isMuted: !this.isMutedOffice,
        reason
      });
      this.isMutedOffice = !this.isMutedOffice;
      this.$bvToast.toast(
        this.isMutedOffice
          ? "事業所をミュートしました"
          : "事業所のミュートを解除しました",
        {
          toastClass: ["toast-custom", "toast-custom-thin-header-page"],
          toaster: "b-toaster-top-full",
          autoHideDelay: 2000
        }
      );
      await this.updateCanApply();
      if (this.isMutedOffice) {
        this.removeMutedOfficeOrderFromOrderStore();
      }
    } catch (error) {
      await showErrorModal(this, error as ErrorType);
    } finally {
      this.$loading.hide();
    }
  }

  async onClickMuteButton() {
    if (!this.isLogin) {
      await this.showLoginModal();
      return;
    }
    if (this.isMutedOffice) {
      // ミュート解除の場合、ダイアログ表示なしで更新実行
      await this.updateMutedOffice();
      return;
    }
    const { canMute, message } = await accountService.canMuteOffice(
      this.orderDetail.office.id
    );
    if (!canMute) {
      const modalMessage = message.split("\n") || [];
      const messageVNode = modalMessage.map(v =>
        h("p", { class: ["text-size-14"] }, [v])
      );
      await this.$bvModal.msgBoxOk(messageVNode, {
        centered: true,
        hideHeader: true,
        okOnly: true
      });
      return;
    }
    this.isShownMutedOfficeModal = true;
  }

  async onSubmitMuteOfficeModal({ reason }: { reason: string }) {
    this.onCloseMuteOfficeModal();
    await this.updateMutedOffice({ reason });
  }

  onCloseMuteOfficeModal() {
    this.isShownMutedOfficeModal = false;
  }

  async createFavoriteOffices() {
    return await favoriteService.createFavoriteOffices({
      office: this.orderDetail.office.id,
      isFavorite: true
    });
  }

  async updateFavoriteOffices() {
    // タップする度に、isFavoriteが反転するように設定
    const isFavorite = !this.favoriteOffice!.isFavorite;
    return await favoriteService.updateFavoriteOffices(
      this.favoriteOffice!.id,
      {
        office: this.orderDetail.office.id,
        isFavorite
      }
    );
  }

  onClickViewRoot() {
    /**
     * HACK: 本来はcordova-plugin-white-listで制限しているため自動で外部ブラウザに移動するが、googleMapのiframeの読み込みが必要なため、
     * googleMapのルートに移動する時だけプラグインを利用する
     *
     */
    openInNewWindow(
      makeGoogleMapConfirmRouteUrl(
        this.orderDetail.office.latitude,
        this.orderDetail.office.longitude
      )
    );
  }

  async onClickApply() {
    // 未ログインユーザーは、確認後、ウェルカム画面へ遷移
    if (!authStore.existToken) {
      const isAccepted = await this.$bvModal.msgBoxConfirm(
        "応募する場合はログインしてください",
        {
          okTitle: "ログインする",
          cancelTitle: "キャンセル",
          cancelVariant: "gray-03",
          centered: true
        }
      );

      if (isAccepted) {
        await this.$router.push({ name: "Welcome" });
      }

      return;
    }

    // 勤務時間が選択されていない場合、エラーメッセージを表示する
    if (!this.isWorkTimeOptionSelected) {
      await this.showErrorMessageWithScrollToWorkTime();
      return;
    }

    sendAdjustEvent("START_APPLICATION");
    sendFirebaseCustomEvent("start_application", {
      order_id: this.orderId
    });

    AbTest3300.registerEvent(AbTest3300.eventTypes.clickApplyButton);
    routerPush(this, {
      name: "OrdersRequirementConfirmation",
      params: {
        orderId: this.orderId,
        workTimeOptionId: this.selectedWorkTimeOptionId!.toString()
      }
    });
  }

  async fetchEvaluations() {
    const evaluation = await officeService.fetchTotalAvgEvaluationsByWorker(
      this.orderDetail.office.id
    );
    this.evaluationCount = evaluation.count;
    this.evaluationTotalAverage = evaluation.totalAverage;
  }

  async fetchBankAccount() {
    try {
      await salaryService.fetchBankAccount();
      this.hasBankAccount = true;
    } catch {
      this.hasBankAccount = false;
    }
  }

  async fetchFavoriteOffice() {
    // 未ログイン時は実行しない
    if (!authStore.existToken) return;

    const {
      results: favoriteOffices
    } = await favoriteService.fetchFavoriteOffices({
      office: this.orderDetail.office.id,
      limit: 1
    });

    if (favoriteOffices.length) {
      this.favoriteOffice = favoriteOffices[0];
    }
  }

  async fetchMutedOffice() {
    const { results } = await accountService.fetchMutedOffices({
      officeId: this.orderDetail.office.id,
      isMuted: true
    });
    this.isMutedOffice = results.length > 0;
  }

  async fetchQualification() {
    this.qualifications = (await accountService.fetchQualifications()).results;
  }

  /**
   * 同じ事業所で、直近勤務可能な案件のを取得する
   */
  async fetchSameOfficeOrder() {
    this.applicableOrders = await orderService.searchOrdersByOfficeIds({
      office__in: this.orderDetail.office.id.toString(),
      date__gte: formatFromUnixTimestamp(startOfToday(), "yyyy-MM-dd"),
      service_name: authStore.workerService.serviceName
    });
  }

  async fetchMyApplication() {
    this.application = await orderService.fetchMyApplicationFromOrderId(
      this.orderDetail.id
    );
  }

  @Watch("selectedWorkTimeOptionId")
  async updateCanApply() {
    if (!this.orderDetail.isHiring) {
      // 応募締切の場合は1件目の時間オプションから canApply を取得し、応募不可理由を表示する
      const res = await this.fetchCanApply(
        this.orderDetail.workTimeOptions[0].id
      );
      this.canApply = res.canApply;
      this.canNotApplyReason = res.canNotApplyReason;
      return;
    }

    if (!this.isWorkTimeOptionSelected) {
      // 応募するボタンを押した段階でチェックするため、ここでは応募できるものとしておく
      this.canApply = true;
      this.canNotApplyReason = "";
      return;
    }
    if (
      !this.hasRegisteredBusinessInfo ||
      !this.hasQualification ||
      !this.hasBankAccount
    ) {
      this.canApply = false;
      this.canNotApplyReason = "";
      return;
    }

    const res = await this.fetchCanApply(Number(this.selectedWorkTimeOptionId));
    this.canApply = res.canApply;
    this.canNotApplyReason = res.canNotApplyReason;
  }

  async fetchCanApply(workTimeOptionId: number): Promise<CanApplyRes> {
    try {
      this.isFetchingCanApply = true;
      await applicationService.fetchCanApply({
        orderId: this.orderDetail.id,
        workTimeOptionId,
        orderVersion: this.orderDetail.version
      });
      return { canApply: true, canNotApplyReason: "" };
    } catch (e) {
      const error = e as ErrorType;
      const defaultCanNotApplyRes: CanApplyRes = {
        canApply: false,
        canNotApplyReason: ""
      };

      if (!error || !("response" in error)) {
        captureException(e as Error);
        return defaultCanNotApplyRes;
      }

      if (!error.response) {
        return defaultCanNotApplyRes;
      }

      if (
        Array.isArray(error.response.data) &&
        error.response.data.length === 1
      ) {
        return {
          canApply: false,
          canNotApplyReason: error.response.data[0]
        };
      } else {
        captureException(e as Error);
        return defaultCanNotApplyRes;
      }
    } finally {
      this.isFetchingCanApply = false;
    }
  }

  async onClickRegistration() {
    const isAccepted = await this.$bvModal.msgBoxConfirm(
      "アカウント認証を完了していないため応募できません",
      {
        okTitle: "アカウント認証する",
        cancelTitle: "キャンセル",
        cancelVariant: "gray-03",
        centered: true
      }
    );
    if (!isAccepted) return;

    routerPush(this, {
      name: "RegistrationInformationAndStatus"
    });
  }

  async onWatchLaterChange() {
    if (!this.isLogin) {
      const isConfirmed = await this.loginModal.showLoginModal();
      if (isConfirmed) {
        await this.$router.push({ name: "Welcome" });
        return;
      }
      return;
    }
    const isRegistered = this.isRegisteredWatchLater;
    if (isRegistered) {
      orderStore.removeFromWatchLaterOrderList(Number(this.orderId));
    } else {
      orderStore.addToWatchLaterOrderList(this.orderDetail);
    }
    this.isRegisteredWatchLater = !isRegistered;
    this.$bvToast.toast(
      isRegistered
        ? "あとで見るリストから削除しました"
        : "あとで見るリストに登録しました",
      {
        toastClass: "toast-custom",
        toaster: "router-link-toaster",
        autoHideDelay: 2000,
        to: "/mypage/watch-later-orders"
      }
    );
    // イベントログを記録する。非同期で十分なのでawaitしない
    eventService.registerEvent(
      AnalyzeWatchLaterEvent.group,
      isRegistered
        ? AnalyzeWatchLaterEvent.types.removeOnDetailPage
        : AnalyzeWatchLaterEvent.types.addOnDetailPage,
      undefined,
      JSON.stringify({
        watchLaterCount: orderStore.watchLaterOrderList.length,
        orderId: parseInt(this.orderId, 10)
      })
    );
  }

  async showErrorMessageWithScrollToWorkTime() {
    // メッセージを先に表示する
    // 画面がスクロールされた後に表示すると、前のスクロール位置に戻されるケースがある
    await this.$bvModal.msgBoxOk("勤務時間を選択してください。", {
      centered: true
    });
    // OrderCard コンポーネントの ref を掴んでいる
    // 親子間の依存はできれば避けたいが、その場合 store を使うなど処理が複雑になりそうだったので、一旦これで進める
    // 原則起こらないはずだが、描画直後などで ref が掴めないケースではエラーになるため、その場合はスクロールは行わない
    const workTimeElement = (this.$refs.orderCard as OrderCard)?.$refs
      ?.workTime as HTMLElement;
    if (workTimeElement) {
      window.scrollTo({
        top: workTimeElement.offsetTop + SCROLL_OFFSET - safeAreaTop,
        behavior: "smooth"
      });
    }
  }
}

export default toNative(OrdersDetail);
</script>

<style lang="scss" scoped>
.paginate {
  margin: -10px;
  zoom: 0.8;

  .button {
    padding: 0.2rem;
  }

  .disabled {
    opacity: 0.2;
  }
}

.order-fetch-failed {
  margin: 2em 0;
  // 上下中央に配置
  font-size: 1.2rem;
  text-align: center;
}
</style>
